import { useMemo } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Type, AlignLeft, Image as ImageIcon, Minus, MessageSquare, MousePointerClick } from 'lucide-react';
import ConfigField from '../ConfigField';

// Internal block shape (stored in node.data.blocks):
//   header   { _id, type: 'header',   text }
//   section  { _id, type: 'section',  text, fields?: [{ title, value }] }
//   divider  { _id, type: 'divider' }
//   image    { _id, type: 'image',    imageUrl, altText, title? }
//   context  { _id, type: 'context',  text }
//   actions  { _id, type: 'actions',  buttons: [{ text, url, style }] }
//
// Backend is responsible for converting this to native Slack Block Kit JSON
// and interpolating {{variables}}.

const BLOCK_TYPES = [
  { type: 'header',  label: 'Header',     icon: Type },
  { type: 'section', label: 'Section',    icon: AlignLeft },
  { type: 'divider', label: 'Divider',    icon: Minus },
  { type: 'image',   label: 'Image',      icon: ImageIcon },
  { type: 'context', label: 'Context',    icon: MessageSquare },
  { type: 'actions', label: 'Buttons',    icon: MousePointerClick },
];

const uid = () => Math.random().toString(36).slice(2, 10);

const makeBlock = (type) => {
  switch (type) {
    case 'header':  return { _id: uid(), type, text: '' };
    case 'section': return { _id: uid(), type, text: '' };
    case 'divider': return { _id: uid(), type };
    case 'image':   return { _id: uid(), type, imageUrl: '', altText: '', title: '' };
    case 'context': return { _id: uid(), type, text: '' };
    case 'actions': return { _id: uid(), type, buttons: [{ text: '', url: '', style: 'default' }] };
    default: return null;
  }
};

const TEXT_FIELD     = { type: 'text' };
const TEXTAREA_FIELD = { type: 'textarea' };

export default function SlackBlocksBuilder({ value, onChange }) {
  const blocks = useMemo(
    () => (Array.isArray(value) ? value : []),
    [value]
  );

  const update = (next) => onChange(next);

  const addBlock = (type) => {
    const block = makeBlock(type);
    if (block) update([...blocks, block]);
  };

  const removeBlock = (idx) => {
    update(blocks.filter((_, i) => i !== idx));
  };

  const moveBlock = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= blocks.length) return;
    const next = blocks.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    update(next);
  };

  const patchBlock = (idx, patch) => {
    const next = blocks.slice();
    next[idx] = { ...next[idx], ...patch };
    update(next);
  };

  return (
    <div className="space-y-3">
      {blocks.length === 0 && (
        <div className="text-xs text-gray-400 italic px-3 py-4 border border-dashed border-gray-200 rounded-lg text-center">
          No blocks yet. Add your first block below.
        </div>
      )}

      {blocks.map((block, idx) => (
        <BlockEditor
          key={block._id || idx}
          block={block}
          index={idx}
          total={blocks.length}
          onChange={(patch) => patchBlock(idx, patch)}
          onRemove={() => removeBlock(idx)}
          onMove={(dir) => moveBlock(idx, dir)}
        />
      ))}

      <div className="flex flex-wrap gap-2 pt-1">
        {BLOCK_TYPES.map(({ type, label, icon: Icon }) => (
          <button
            key={type}
            type="button"
            onClick={() => addBlock(type)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-gray-200 rounded-md hover:bg-gray-50 hover:border-gray-300 transition-colors"
          >
            <Icon size={12} />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function BlockEditor({ block, index, total, onChange, onRemove, onMove }) {
  const meta = BLOCK_TYPES.find((b) => b.type === block.type);
  const Icon = meta?.icon || AlignLeft;

  return (
    <div className="border border-gray-200 rounded-lg bg-gray-50/50 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2 text-xs font-semibold text-gray-700">
          <Icon size={13} />
          <span>{meta?.label || block.type}</span>
          <span className="text-gray-400 font-normal">#{index + 1}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={index === 0}
            onClick={() => onMove(-1)}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move up"
          >
            <ChevronUp size={14} />
          </button>
          <button
            type="button"
            disabled={index === total - 1}
            onClick={() => onMove(1)}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move down"
          >
            <ChevronDown size={14} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="p-1 rounded hover:bg-red-50 text-red-500"
            title="Remove block"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="p-3 space-y-3">
        <BlockBody block={block} onChange={onChange} />
      </div>
    </div>
  );
}

function Label({ children }) {
  return <label className="block text-[11px] font-medium text-gray-600 mb-1">{children}</label>;
}

function BlockBody({ block, onChange }) {
  if (block.type === 'header') {
    return (
      <div>
        <Label>Header text</Label>
        <ConfigField
          field={{ ...TEXT_FIELD, placeholder: 'Section title' }}
          value={block.text}
          onChange={(v) => onChange({ text: v })}
        />
      </div>
    );
  }

  if (block.type === 'section') {
    const fields = Array.isArray(block.fields) ? block.fields : [];
    const setFields = (next) => onChange({ fields: next });

    return (
      <>
        <div>
          <Label>Markdown text</Label>
          <ConfigField
            field={{ ...TEXTAREA_FIELD, placeholder: '*Bold*, _italic_, `code`, <{{user.email}}>' }}
            value={block.text}
            onChange={(v) => onChange({ text: v })}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <Label>Fields (two-column)</Label>
            <button
              type="button"
              onClick={() => setFields([...fields, { title: '', value: '' }])}
              className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-700"
            >
              <Plus size={11} /> Add field
            </button>
          </div>
          {fields.length > 0 && (
            <div className="space-y-2">
              {fields.map((f, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-start">
                  <ConfigField
                    field={{ ...TEXT_FIELD, placeholder: 'Title' }}
                    value={f.title}
                    onChange={(v) => setFields(fields.map((x, j) => (i === j ? { ...x, title: v } : x)))}
                  />
                  <ConfigField
                    field={{ ...TEXT_FIELD, placeholder: 'Value' }}
                    value={f.value}
                    onChange={(v) => setFields(fields.map((x, j) => (i === j ? { ...x, value: v } : x)))}
                  />
                  <button
                    type="button"
                    onClick={() => setFields(fields.filter((_, j) => j !== i))}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </>
    );
  }

  if (block.type === 'divider') {
    return <p className="text-xs text-gray-400 italic">Renders a horizontal separator.</p>;
  }

  if (block.type === 'image') {
    return (
      <>
        <div>
          <Label>Image URL</Label>
          <ConfigField
            field={{ ...TEXT_FIELD, placeholder: 'https://...' }}
            value={block.imageUrl}
            onChange={(v) => onChange({ imageUrl: v })}
          />
        </div>
        <div>
          <Label>Alt text</Label>
          <ConfigField
            field={{ ...TEXT_FIELD, placeholder: 'Describe the image' }}
            value={block.altText}
            onChange={(v) => onChange({ altText: v })}
          />
        </div>
        <div>
          <Label>Title (optional)</Label>
          <ConfigField
            field={{ ...TEXT_FIELD, placeholder: 'Caption above image' }}
            value={block.title}
            onChange={(v) => onChange({ title: v })}
          />
        </div>
      </>
    );
  }

  if (block.type === 'context') {
    return (
      <div>
        <Label>Context text (small, muted)</Label>
        <ConfigField
          field={{ ...TEXTAREA_FIELD, placeholder: 'Triggered by {{user.email}} at {{timestamp}}' }}
          value={block.text}
          onChange={(v) => onChange({ text: v })}
        />
      </div>
    );
  }

  if (block.type === 'actions') {
    const buttons = Array.isArray(block.buttons) ? block.buttons : [];
    const setButtons = (next) => onChange({ buttons: next });

    return (
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label>Buttons</Label>
          <button
            type="button"
            onClick={() => setButtons([...buttons, { text: '', url: '', style: 'default' }])}
            className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-700"
          >
            <Plus size={11} /> Add button
          </button>
        </div>
        <div className="space-y-2">
          {buttons.map((b, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_120px_auto] gap-2 items-start">
              <ConfigField
                field={{ ...TEXT_FIELD, placeholder: 'Label' }}
                value={b.text}
                onChange={(v) => setButtons(buttons.map((x, j) => (i === j ? { ...x, text: v } : x)))}
              />
              <ConfigField
                field={{ ...TEXT_FIELD, placeholder: 'https://...' }}
                value={b.url}
                onChange={(v) => setButtons(buttons.map((x, j) => (i === j ? { ...x, url: v } : x)))}
              />
              <ConfigField
                field={{
                  type: 'select',
                  options: [
                    { value: 'default', label: 'Default' },
                    { value: 'primary', label: 'Primary' },
                    { value: 'danger',  label: 'Danger'  },
                  ],
                }}
                value={b.style}
                onChange={(v) => setButtons(buttons.map((x, j) => (i === j ? { ...x, style: v } : x)))}
              />
              <button
                type="button"
                onClick={() => setButtons(buttons.filter((_, j) => j !== i))}
                className="p-1.5 text-red-500 hover:bg-red-50 rounded"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return <p className="text-xs text-gray-400">Unknown block type: {block.type}</p>;
}
